import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {copyToClipboard} from 'nutshell-core/utils/copy-to-clipboard';

import {EditionThumbnailList} from 'shells/edition-thumbnail-list/edition-thumbnail-list';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
// @ts-expect-error - imported from .js/.jsx file
import {ArchiveIcon} from 'shells/icon/svg/archive-icon';
// @ts-expect-error - imported from .js/.jsx file
import {CloneIcon} from 'shells/icon/svg/clone-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ExternalLinkIcon} from 'shells/icon/svg/external-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {EditionStubFragment as Edition} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    isViewingArchived?: boolean;
    editions: Edition[];
    onDuplicateEdition?: (
        editionId: string,
        campaignId: string,
        name?: string | null | undefined
    ) => Promise<any>;
    onDeleteEdition?: (editionId: string, campaignId: string) => Promise<any>;
    onRestoreEdition?: (editionId: string, campaignId: string) => Promise<any>;
    isLoading: boolean;
    setIsAddingNewEdition: (isAddingNewEdition: boolean) => void;
};

export function NewsletterEditions(props: Props) {
    const {
        isViewingArchived = false,
        editions,
        onDuplicateEdition,
        onDeleteEdition,
        onRestoreEdition,
        isLoading,
        setIsAddingNewEdition,
    } = props;

    const newsletterId: string = props.match.params.newsletterId;
    const [duplicatingEdition, setDuplicatingEdition] = React.useState<Edition | null | undefined>(
        null
    );
    const [isDuplicatingEdition, setIsDuplicatingEdition] = React.useState<boolean>(false);

    if (isLoading) {
        return <LoadingPage />;
    }

    if (editions.length === 0 && isViewingArchived) {
        return (
            <NothingFoundEmptyState
                title='Nothing to see here'
                subtitle='This is where you’ll find archived editions'
            />
        );
    }

    return (
        <>
            <EditionThumbnailList
                onSelect={(editionId) => {
                    // @ts-expect-error
                    const selectedEdition = editions.find((edition) => {
                        return edition.id === editionId;
                    });

                    if (selectedEdition) {
                        appHistory.push(selectedEdition.htmlUrlPath);
                    }
                }}
                // @ts-expect-error
                editions={editions.map((edition) => {
                    const options = [
                        {
                            label: 'View',
                            handleSelect: () => {
                                appHistory.push(edition.htmlUrlPath);
                            },
                            icon: <EyeIcon wrapWithDiv={false} size={18} />,
                        },
                    ];

                    if (isViewingArchived && onRestoreEdition) {
                        options.push({
                            label: 'Restore',
                            handleSelect: () => {
                                onRestoreEdition(edition.id, newsletterId);
                            },
                            icon: <ArchiveIcon size={16} />,
                        });
                    }

                    if (!isViewingArchived) {
                        if (onDuplicateEdition) {
                            options.push({
                                label: 'Make a copy',
                                handleSelect: () => {
                                    setDuplicatingEdition(edition);
                                },
                                icon: <CloneIcon size={16} />,
                            });
                        }

                        options.push({
                            label: 'Copy shareable URL',
                            handleSelect: () => {
                                copyToClipboard(edition.viewInBrowserLink);
                            },
                            // @ts-expect-error
                            tooltipTextOnClose: 'Copied!',
                            icon: <ExternalLinkIcon size={16} />,
                        });

                        if (edition.status === 'NEW' && onDeleteEdition) {
                            options.push({
                                label: 'Archive',
                                // @ts-expect-error
                                variant: 'destructive',
                                handleSelect: () => {
                                    onDeleteEdition?.(edition.id, newsletterId);
                                },
                                icon: <ArchiveIcon size={16} />,
                            });
                        }
                    }

                    return {
                        ...edition,
                        status: isViewingArchived ? 'ARCHIVED' : edition.status,
                        thumbnailUrl:
                            edition.emailTemplate && edition.emailTemplate.thumbnailUrl
                                ? edition.emailTemplate.thumbnailUrl
                                : undefined,
                        options: options,
                        campaignType: 'NEWSLETTER',
                    };
                })}
                addNew={
                    !isViewingArchived && editions.length === 0
                        ? {
                              onClick: () => {
                                  setIsAddingNewEdition(true);
                              },
                              text: 'Create new edition',
                          }
                        : undefined
                }
                isLoading={isLoading}
            />
            {duplicatingEdition ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Create'
                    initialValue={`Copy of ${duplicatingEdition.name}`}
                    title='Name your new edition'
                    subtitle='Add a descriptive name so you can find it later'
                    placeholder={`Copy of ${duplicatingEdition.name}`}
                    isSaving={isDuplicatingEdition}
                    onClose={() => {
                        setDuplicatingEdition(null);
                    }}
                    onSave={(name) => {
                        setIsDuplicatingEdition(true);
                        const nameValue = name && name.length ? name : null;
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        onDuplicateEdition &&
                            onDuplicateEdition(duplicatingEdition.id, newsletterId, nameValue).then(
                                () => {
                                    setIsDuplicatingEdition(false);
                                    setDuplicatingEdition(null);
                                }
                            );
                    }}
                />
            ) : undefined}
        </>
    );
}
